import React, { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import '../css/Modal.css';

const Modal = ({
  image,
  images = undefined,
  alts = undefined,
  currentIndex = 0,
  onSelectImage = undefined,
  onClose,
  title,
  language = 'en',
}) => {
  const closeButtonRef = useRef(null);
  const contentRef = useRef(null);
  const imageList = Array.isArray(images) && images.length > 0 ? images : [image].filter(Boolean);
  const safeCurrentIndex = Math.max(0, Math.min(currentIndex, imageList.length - 1));
  const currentImage = imageList[safeCurrentIndex] || '';
  const hasMultipleImages = imageList.length > 1 && typeof onSelectImage === 'function';

  const selectImage = useCallback(
    (nextIndex) => {
      if (!hasMultipleImages) {
        return;
      }

      const wrappedIndex = (nextIndex + imageList.length) % imageList.length;
      onSelectImage(wrappedIndex);
    },
    [hasMultipleImages, imageList.length, onSelectImage]
  );

  const showPreviousImage = useCallback(() => {
    selectImage(safeCurrentIndex - 1);
  }, [safeCurrentIndex, selectImage]);

  const showNextImage = useCallback(() => {
    selectImage(safeCurrentIndex + 1);
  }, [safeCurrentIndex, selectImage]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        showPreviousImage();
        return;
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        showNextImage();
        return;
      }

      // Keep Tab focus inside the dialog (aria-modal promises this).
      if (event.key === 'Tab') {
        const container = contentRef.current;
        if (!container) {
          return;
        }

        const focusables = container.querySelectorAll(
          'button:not(:disabled), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusables.length === 0) {
          return;
        }

        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement;

        if (event.shiftKey) {
          if (active === first || !container.contains(active)) {
            event.preventDefault();
            last.focus();
          }
        } else if (active === last || !container.contains(active)) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [onClose, showNextImage, showPreviousImage]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previouslyFocused = document.activeElement;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused?.isConnected && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, []);

  if (typeof document === 'undefined') {
    return null;
  }

  const copy =
    language === 'ja'
      ? {
          close: '閉じる',
          previous: '前の画像',
          next: '次の画像',
          counter: `${safeCurrentIndex + 1} / ${imageList.length}`,
        }
      : {
          close: 'Close',
          previous: 'Previous image',
          next: 'Next image',
          counter: `${safeCurrentIndex + 1} / ${imageList.length}`,
        };

  return createPortal(
    <div className="modal-overlay" onClick={onClose} role="presentation">
      <div
        className="modal-content"
        ref={contentRef}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title || 'Expanded image'}
      >
        <button
          type="button"
          ref={closeButtonRef}
          onClick={onClose}
          className="close-modal"
          aria-label={copy.close}
        >
          {copy.close}
        </button>

        {hasMultipleImages ? (
          <button
            type="button"
            className="modal-nav modal-nav--prev"
            onClick={showPreviousImage}
            aria-label={copy.previous}
          >
            <span aria-hidden="true">‹</span>
          </button>
        ) : null}

        <img
          src={currentImage}
          alt={
            (Array.isArray(alts) && alts[safeCurrentIndex]) ||
            (title ? `${title} screenshot ${safeCurrentIndex + 1}` : 'Expanded project view')
          }
        />

        {hasMultipleImages ? (
          <button
            type="button"
            className="modal-nav modal-nav--next"
            onClick={showNextImage}
            aria-label={copy.next}
          >
            <span aria-hidden="true">›</span>
          </button>
        ) : null}

        {hasMultipleImages ? <p className="modal-counter">{copy.counter}</p> : null}
      </div>
    </div>,
    document.body
  );
};

export default Modal;
